import React, { useState } from "react";
import {
  useCapitalDistributions,
  useCreateCapitalDistribution,
  useUpdateCapitalDistribution,
  useDeleteCapitalDistribution,
} from "@/hooks/useCapitalDistributions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { isApiError } from "@/api/client";
import { formatDate } from "@/lib/utils";
import type { CapitalDistribution } from "@/types";

const fmtBRL = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

// Ordinal em inglês: 1st, 2nd, 3rd, 4th… As exceções 11/12/13 (eleventh,
// twelfth, thirteenth) usam "th" apesar de terminarem em 1/2/3.
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

interface Props {
  fundId: string;
  isAdmin: boolean;
}

export function CapitalDistributionsTab({ fundId, isAdmin }: Props) {
  const { data: distributions, isLoading } = useCapitalDistributions(fundId);
  const createDist = useCreateCapitalDistribution(fundId);
  const updateDist = useUpdateCapitalDistribution(fundId);
  const deleteDist = useDeleteCapitalDistribution(fundId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CapitalDistribution | null>(null);
  const [draftDate, setDraftDate] = useState("");
  // Valor guardado em centavos (inteiro), no mesmo estilo calculadora usado nas
  // chamadas de capital: os dígitos entram da direita pra esquerda.
  const [draftCents, setDraftCents] = useState(0);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const centsDisplay = (cents: number) =>
    (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key >= "0" && e.key <= "9") {
      e.preventDefault();
      // teto defensivo: ~R$ 90 trilhões, evita estourar a precisão de number
      const next = draftCents * 10 + Number(e.key);
      if (next <= 9_000_000_000_000_000) setDraftCents(next);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      setDraftCents(Math.floor(draftCents / 10));
    }
  };

  const sorted = [...(distributions ?? [])].sort(
    (a, b) => a.distribution_number - b.distribution_number
  );
  const totalBrl = sorted.reduce((s, d) => s + Number(d.amount_brl ?? 0), 0);

  const openAdd = () => {
    setEditing(null);
    setDraftDate("");
    setDraftCents(0);
    setDialogOpen(true);
  };

  const openEdit = (d: CapitalDistribution) => {
    setEditing(d);
    setDraftDate(d.distribution_date ? d.distribution_date.slice(0, 10) : "");
    setDraftCents(Math.round(Number(d.amount_brl) * 100));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const amount = draftCents / 100;
    if (!draftDate) { toast.error("Enter the date"); return; }
    if (!amount || amount <= 0) { toast.error("Enter an amount greater than zero"); return; }
    try {
      if (editing) {
        await updateDist.mutateAsync({ id: editing.id, distribution_date: draftDate, amount_brl: amount });
        toast.success("Distribution updated");
      } else {
        await createDist.mutateAsync({ distribution_date: draftDate, amount_brl: amount });
        toast.success("Distribution created");
      }
      setDialogOpen(false);
    } catch (e) {
      const msg = isApiError(e) ? e.response?.data?.error ?? "Error saving" : "Error saving";
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDist.mutateAsync(deleteId);
      toast.success("Distribution removed");
    } catch {
      toast.error("Error removing");
    }
    setDeleteId(null);
  };

  const thS: React.CSSProperties = {
    backgroundColor: "rgb(212, 163, 123)",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "11px",
    padding: "6px 8px",
    whiteSpace: "nowrap",
    textAlign: "center",
  };
  const tdS: React.CSSProperties = {
    fontSize: "12px",
    padding: "5px 8px",
    whiteSpace: "nowrap",
    borderBottom: "1px solid hsl(var(--border))",
  };
  const tdCS: React.CSSProperties = { ...tdS, textAlign: "center" };
  const tdRS: React.CSSProperties = { ...tdS, textAlign: "right" };
  const totS: React.CSSProperties = {
    backgroundColor: "rgb(0, 56, 130)",
    color: "#e2eaf5",
    fontWeight: 700,
    fontSize: "12px",
    padding: "6px 8px",
    whiteSpace: "nowrap",
  };
  const totRS: React.CSSProperties = { ...totS, textAlign: "right" };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Distributed: {fmtBRL(totalBrl)}
          </span>
          <Button size="sm" onClick={openAdd} className="gap-2">
            <Plus className="h-4 w-4" /> New Distribution
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-x-auto">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: "center" }}>Distribution</th>
              <th style={{ ...thS, textAlign: "center" }}>Date</th>
              <th style={{ ...thS, textAlign: "right" }}>Amount (BRL)</th>
              {isAdmin && <th style={{ ...thS, width: "64px" }} />}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td style={tdCS} colSpan={isAdmin ? 4 : 3}>Loading…</td></tr>
            )}
            {!isLoading && sorted.length === 0 && (
              <tr><td style={{ ...tdCS, color: "hsl(var(--muted-foreground))" }} colSpan={isAdmin ? 4 : 3}>
                No distributions recorded.
              </td></tr>
            )}
            {sorted.map((d, idx) => (
              <tr key={d.id} style={{ backgroundColor: idx % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)" }}>
                <td style={{ ...tdCS, fontWeight: 600 }}>{ordinal(d.distribution_number)}</td>
                <td style={tdCS}>{formatDate(d.distribution_date)}</td>
                <td style={tdRS}>{fmtBRL(Number(d.amount_brl ?? 0))}</td>
                {isAdmin && (
                  <td style={{ ...tdS, padding: "2px 4px" }}>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(d)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteId(d.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {sorted.length > 0 && (
              <tr>
                <td style={{ ...totS, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }} colSpan={2}>
                  Total ({sorted.length})
                </td>
                <td style={totRS}>{fmtBRL(totalBrl)}</td>
                {isAdmin && <td style={totS} />}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${ordinal(editing.distribution_number)} distribution` : "New Distribution"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Date</span>
              <Input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Amount (BRL)</span>
              <Input
                type="text"
                inputMode="numeric"
                value={centsDisplay(draftCents)}
                onKeyDown={handleAmountKeyDown}
                onChange={() => { /* edição controlada por onKeyDown */ }}
                className="h-9 text-right"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createDist.isPending || updateDist.isPending}>
              {editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove distribution?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The numbering of the other distributions will not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
